import React, { useState } from "react";
import { API } from "../lib/api";
import { rarityMeta, cardGradient, RARITY_FULL } from "../lib/helpers";

const MAX_RETRY = 3;

// One Piece cards are ~5:7. object-contain keeps full art (no crop). Images are served
// through the backend proxy (/api/img/{variant_id}) to bypass cross-origin CORP blocking.
export default function CardFace({ card, imagesEnabled = true, showSample = true, onClick, className = "" }) {
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [attempt, setAttempt] = useState(0);
  const meta = rarityMeta(card.rarity);
  const useImg = imagesEnabled && card.variant_id && status !== "error";
  const src = `${API}/img/${card.variant_id}${attempt > 0 ? `?r=${attempt}` : ""}`;

  const onError = () => {
    if (attempt < MAX_RETRY) {
      setTimeout(() => setAttempt((a) => a + 1), 500 + attempt * 500);
    } else {
      setStatus("error");
    }
  };

  return (
    <div
      className={`relative w-full aspect-[5/7] rounded-2xl overflow-hidden select-none ${onClick ? "cursor-pointer" : ""} ${className}`}
      onClick={onClick}
      data-testid={`card-face-${card.number}`}
    >
      {useImg ? (
        <div className="absolute inset-0 bg-slate-900">
          <img
            key={attempt}
            src={src}
            alt={`${card.name} ${card.number}`}
            loading="lazy"
            onLoad={() => setStatus("ok")}
            onError={onError}
            className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-300 ${status === "ok" ? "opacity-100" : "opacity-0"}`}
          />
          {status !== "ok" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-slate-800 to-slate-900">
              <div className="w-8 h-8 rounded-full border-2 border-white/30 border-t-white/80 animate-spin" />
              <span className="text-[9px] font-mono text-white/50">{card.number}</span>
            </div>
          )}
        </div>
      ) : (
        <div
          className="absolute inset-0 flex flex-col justify-between p-2 text-white"
          style={{ background: cardGradient(card.colors) }}
        >
          <div className="flex items-center justify-between text-[10px] font-bold opacity-90">
            <span className="font-mono">{card.number}</span>
            <span className="px-1.5 py-0.5 rounded-full bg-white/25">{meta.label}</span>
          </div>
          <div className="text-center px-1">
            <div className="font-display font-bold leading-tight text-[13px] drop-shadow">{card.name}</div>
            <div className="text-[9px] mt-1 opacity-80">{RARITY_FULL[card.rarity] || card.source_rarity}</div>
          </div>
          <div className="text-[8px] text-center opacity-70 font-semibold uppercase tracking-wide">
            Immagine non disponibile
          </div>
        </div>
      )}

      {showSample && useImg && status === "ok" && (
        <div className="sample-overlay">
          <span>SAMPLE · SAMPLE · SAMPLE</span>
        </div>
      )}

      {card.quantity > 1 && (
        <div className="absolute top-1.5 right-1.5 z-20 chip bg-amber-400 text-amber-950 border-amber-300 !text-[10px] !px-2 !py-0.5">
          ×{card.quantity}
        </div>
      )}
      {card.is_base === false && (
        <div className="absolute bottom-1.5 left-1.5 z-20 chip bg-purple-600 text-white !text-[9px] !px-2 !py-0.5">
          Variante
        </div>
      )}
    </div>
  );
}
